/**
 * 평가계획 성취기준·평가계획 번들 IndexedDB 저장소 (programWorkspaceIdb 패턴과 동일).
 * 관리자 성취기준(수백 행·긴 텍스트)은 LocalStorage 용량을 압박하므로 IDB 에 버전(`count:max(updated_at)`)과 함께 보관.
 * IDB 불가(사파리 개인정보 보호 모드 등) 시 모든 호출이 조용히 실패 → 호출부는 네트워크로 폴백.
 */
import type { EvaluationPlanCategoryBundle } from '../types/evaluationPlan';

const DB_NAME = 'ClassPlannerEvaluationPlanBundles';
const DB_VERSION = 1;
const STORE_BUNDLES = 'bundles';

export type EvaluationPlanBundleIdbEntry = {
  bundle: EvaluationPlanCategoryBundle;
  contentVersion: string;
  savedAt: number;
};

function idbOpen(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('indexedDB unavailable'));
      return;
    }
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onerror = () => reject(req.error ?? new Error('indexedDB open failed'));
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_BUNDLES)) db.createObjectStore(STORE_BUNDLES);
    };
    req.onsuccess = () => resolve(req.result);
  });
}

async function idbTx<T>(mode: IDBTransactionMode, run: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  const db = await idbOpen();
  try {
    return await new Promise<T>((resolve, reject) => {
      const tx = db.transaction(STORE_BUNDLES, mode);
      const req = run(tx.objectStore(STORE_BUNDLES));
      req.onerror = () => reject(req.error ?? new Error('indexedDB request failed'));
      req.onsuccess = () => resolve(req.result as T);
    });
  } finally {
    db.close();
  }
}

export async function idbGetEvaluationPlanBundle(key: string): Promise<EvaluationPlanBundleIdbEntry | undefined> {
  try {
    const v = await idbTx<EvaluationPlanBundleIdbEntry | undefined>('readonly', (s) => s.get(key));
    if (!v || typeof v !== 'object' || !v.bundle || typeof v.contentVersion !== 'string') return undefined;
    return v;
  } catch {
    return undefined;
  }
}

export async function idbPutEvaluationPlanBundle(key: string, entry: EvaluationPlanBundleIdbEntry): Promise<void> {
  try {
    await idbTx('readwrite', (s) => s.put(entry, key));
  } catch {
    /* quota / private mode */
  }
}

export async function idbDeleteEvaluationPlanBundle(key: string): Promise<void> {
  try {
    await idbTx('readwrite', (s) => s.delete(key));
  } catch {
    /* ignore */
  }
}
